import {
  AUTH_STORAGE_KEY,
  applyAuthToken,
  getStoredAuth,
} from "./auth_storage";
import {
  isImpersonationSession,
  refresh as refreshAuthToken,
} from "../services/authService";

/** Refresh access JWT this many ms before `exp` (silent / proactive). */
const REFRESH_SKEW_MS = 2 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 5_000;

type QueuedRequest = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let keeperStarted = false;

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((queuedRequest) => {
    if (error) {
      queuedRequest.reject(error);
      return;
    }

    if (token) {
      queuedRequest.resolve(token);
      return;
    }

    queuedRequest.reject(new Error("refresh_failed"));
  });

  failedQueue = [];
};

export const decodeJwtExpSeconds = (token: string): number | null => {
  try {
    const parts = token.split(".");

    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    ) as { exp?: number };

    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
};

export const shouldAttemptTokenRefresh = (): boolean => {
  const stored = getStoredAuth();

  if (!stored?.refreshToken) {
    return false;
  }

  return !isImpersonationSession();
};

export const isAccessTokenExpiringSoon = (
  token: string,
  skewMs = REFRESH_SKEW_MS
): boolean => {
  const exp = decodeJwtExpSeconds(token);

  if (exp === null) {
    return true;
  }

  return Date.now() >= exp * 1000 - skewMs;
};

/**
 * Single-flight access renewal. Persists rotated refresh via authService.refresh.
 * Recovers when another tab already rotated the opaque refresh token.
 */
export const ensureFreshAccessToken = async (options?: {
  force?: boolean;
}): Promise<string | null> => {
  if (isImpersonationSession()) {
    return getStoredAuth()?.token ?? null;
  }

  const stored = getStoredAuth();

  if (!stored?.refreshToken) {
    return stored?.token ?? null;
  }

  if (
    !options?.force &&
    stored.token &&
    !isAccessTokenExpiringSoon(stored.token)
  ) {
    return stored.token;
  }

  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  const refreshTokenUsed = stored.refreshToken;

  try {
    const latestBefore = getStoredAuth();

    if (
      !options?.force &&
      latestBefore?.token &&
      latestBefore.refreshToken &&
      !isAccessTokenExpiringSoon(latestBefore.token)
    ) {
      processQueue(null, latestBefore.token);
      return latestBefore.token;
    }

    const refreshToken = latestBefore?.refreshToken ?? refreshTokenUsed;

    if (!refreshToken) {
      processQueue(new Error("missing_refresh_token"), null);
      return null;
    }

    const refreshResponse = await refreshAuthToken(refreshToken);

    if (refreshResponse.success && refreshResponse.data?.access_token) {
      const newToken = refreshResponse.data.access_token;
      processQueue(null, newToken);
      scheduleSilentRefresh();
      return newToken;
    }

    const afterFail = getStoredAuth();

    if (
      afterFail?.token &&
      afterFail.refreshToken &&
      afterFail.refreshToken !== refreshToken
    ) {
      processQueue(null, afterFail.token);
      scheduleSilentRefresh();
      return afterFail.token;
    }

    processQueue(new Error("refresh_failed"), null);
    return null;
  } catch (error) {
    const afterFail = getStoredAuth();

    if (
      afterFail?.token &&
      afterFail.refreshToken &&
      afterFail.refreshToken !== refreshTokenUsed
    ) {
      processQueue(null, afterFail.token);
      scheduleSilentRefresh();
      return afterFail.token;
    }

    processQueue(error, null);
    return null;
  } finally {
    isRefreshing = false;
  }
};

export const scheduleSilentRefresh = (): void => {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  if (isImpersonationSession()) {
    return;
  }

  const auth = getStoredAuth();

  if (!auth?.token || !auth.refreshToken) {
    return;
  }

  const exp = decodeJwtExpSeconds(auth.token);

  if (exp === null) {
    return;
  }

  const delay = Math.max(
    MIN_REFRESH_DELAY_MS,
    exp * 1000 - Date.now() - REFRESH_SKEW_MS
  );

  refreshTimer = setTimeout(() => {
    void ensureFreshAccessToken({ force: true });
  }, delay);
};

/**
 * Proactive silent refresh while the Velzon shell is mounted.
 * Returns a cleanup function for React unmount.
 */
export const startTokenSessionKeeper = (): (() => void) => {
  if (keeperStarted) {
    return () => undefined;
  }

  keeperStarted = true;
  scheduleSilentRefresh();
  void ensureFreshAccessToken();

  const onVisibility = (): void => {
    if (document.visibilityState === "visible") {
      void ensureFreshAccessToken().then((token) => {
        if (token) {
          scheduleSilentRefresh();
        }
      });
    }
  };

  const onFocus = (): void => {
    void ensureFreshAccessToken().then((token) => {
      if (token) {
        scheduleSilentRefresh();
      }
    });
  };

  const onStorage = (event: StorageEvent): void => {
    if (event.key !== AUTH_STORAGE_KEY || !event.newValue) {
      return;
    }

    try {
      const parsed = JSON.parse(event.newValue) as { token?: string };

      if (parsed.token) {
        applyAuthToken(parsed.token);
        scheduleSilentRefresh();
      }
    } catch {
      // Ignore malformed cross-tab payloads.
    }
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", onFocus);
  window.addEventListener("storage", onStorage);

  return () => {
    keeperStarted = false;

    if (refreshTimer !== null) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }

    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("storage", onStorage);
  };
};
